import React, { useState, useEffect, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import { useQueryClient } from "react-query";
import { ReactSelect } from "@anchor/react-components/dist/lib/components";
import { McIcon, McTooltip } from "@maersk-global/mds-react-wrapper/components-core";
import { ApprovalWorkflowServices } from "../../services/approvalWorkflowServices";
import { FetchUserProfileService } from "../../services/fetchUserProfileService";
import { cachedRequest } from "../../Utils/cachedRequest";
import { formatProfiles } from "../Collaborators/helpers/formatProfiles";
import errorNotification from "../Notification/errorNotification";
import "./MailRecipientsCell.css";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Editable "Mail Recipients" metadata cell — shows who will receive the
 * Leadership Review email (Platform Owners + Portfolio/TLT Owner), populated
 * once the Caused By CI form (Portfolio + Platform) has been saved. Lets the
 * user add/remove recipients before submitting for review.
 */
function MailRecipientsCell({ postMortemId, portfolio, platform, isEditable, refreshTick }) {
  const { instance, accounts } = useMsal();
  const { getRecipientsPreview, saveManualRecipients } = ApprovalWorkflowServices();
  const queryClient = useQueryClient();

  const [recipients, setRecipients] = useState(null); // null = not yet loaded
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [userOptions, setUserOptions] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [inputError, setInputError] = useState("");
  const { fetchUserProfile } = FetchUserProfileService();

  const canLoad = postMortemId && portfolio && platform;

  const loadPreview = useCallback(async () => {
    if (!canLoad) return;
    setLoading(true);
    try {
      // Keyed by postmortem only: dropdown changes do not change the saved recipients, and saves clear this cache.
      const result = await cachedRequest(queryClient, ["recipientsPreview", postMortemId], () => getRecipientsPreview(postMortemId));
      setRecipients(result?.ready ? (result.recipients || []) : []);
      setAlreadySubmitted(!!result?.alreadySubmitted);
    } catch (error) {
      setRecipients([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postMortemId, portfolio, platform, canLoad]);

  useEffect(() => {
    loadPreview();
    // Portfolio/Platform only reach the DB once the user clicks Save, so also refetch
    // whenever a save completes (refreshTick) — the dropdown value alone changes instantly
    // but the backend still reads the pre-save value until then.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadPreview, refreshTick]);

  const persist = async (nextRecipients) => {
    setSaving(true);
    try {
      await saveManualRecipients(instance, accounts, postMortemId, nextRecipients);
    } catch (error) {
      errorNotification("Failed to update mail recipients");
      // Revert to last known-good state on failure
      loadPreview();
    } finally {
      setSaving(false);
    }
  };

  const handleAdd = (selectedEmail) => {
    const email = selectedEmail.trim().toLowerCase();
    if (!email) return;
    if (!EMAIL_REGEX.test(email)) {
      setInputError("Enter a valid email address");
      return;
    }
    if (recipients.includes(email)) {
      setInputError("Already in the list");
      return;
    }
    const next = [...recipients, email];
    setRecipients(next);
    setSelectedUser(null);
    setInputError("");
    persist(next);
  };

  const searchUsers = async (searchText) => {
    if (!searchText || searchText.trim().length === 0) {
      setUserOptions([]);
      return;
    }

    setUserSearchLoading(true);
    try {
      const response = await fetchUserProfile(searchText.trim());
      setUserOptions(formatProfiles(response?.value || []));
    } catch (error) {
      setUserOptions([]);
    } finally {
      setUserSearchLoading(false);
    }
  };

  const handleRemove = (email) => {
    const next = recipients.filter((r) => r !== email);
    setRecipients(next);
    persist(next);
  };

  const renderApproversTitle = () => (
    <span className="mail-recipients-title">
      Postmortem Approvers
      <McTooltip position="bottom-right" fit="small" width={300}>
        <McIcon slot="trigger" icon="info-circle" size="16" className="mail-recipients-info-icon" />
        <div className="mail-recipients-info-text">
          Approvers are selected from the Caused By Platform and Portfolio. Anyone listed here can approve the postmortem and move it forward from Leadership Review.
        </div>
      </McTooltip>
    </span>
  );

  if (!canLoad) {
    return (
      <>
        <div className="metaData_colHead">{renderApproversTitle()}</div>
        <div className="metaData_colBody">
          <i style={{ color: "#aaa" }}>Fill Caused By CI form to populate Postmortem Approvers</i>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="metaData_colHead">
        {renderApproversTitle()}
        {saving && <span className="mail-recipients-saving">Saving...</span>}
        {!loading && (
          <button
            type="button"
            className="mail-recipients-refresh"
            title="Refresh recipients (e.g. after correcting Caused By CI and saving)"
            onClick={loadPreview}
          >
            &#8635;
          </button>
        )}
      </div>
      <div className="metaData_colBody">
        {loading || recipients === null ? (
          <i style={{ color: "#aaa" }}>Loading...</i>
        ) : (
          <div className="mail-recipients-list">
            {alreadySubmitted && (
              <span className="mail-recipients-note">Already notified — showing who received the email</span>
            )}
            {recipients.length === 0 && (
              <i style={{ color: "#aaa" }}>No recipients found</i>
            )}
            {recipients.map((email) => (
              <span key={email} className="mail-recipient-chip">
                {email}
                {isEditable && !alreadySubmitted && (
                  <button
                    type="button"
                    className="mail-recipient-remove"
                    title={`Remove ${email}`}
                    onClick={() => handleRemove(email)}
                  >
                    &times;
                  </button>
                )}
              </span>
            ))}
            {isEditable && !alreadySubmitted && (
              <div className="mail-recipient-add-row">
                <ReactSelect
                  className="mail-recipient-picker"
                  classNamePrefix="mail-recipient-picker"
                  options={userOptions}
                  isSearchable
                  isClearable
                  isLoading={userSearchLoading}
                  noOptionsMessage={() => "Type a name or email to search"}
                  placeholder="Add email..."
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  onInputChange={(value, meta) => {
                    if (meta.action === "input-change") {
                      setInputError("");
                      searchUsers(value);
                    }
                    return value;
                  }}
                  onChange={(option) => {
                    setSelectedUser(option || null);
                    setInputError("");
                  }}
                  value={selectedUser}
                  styles={{
                    container: (base) => ({ ...base, width: "150px" }),
                    control: (base) => ({ ...base, minHeight: "36px", borderRadius: "18px" }),
                    valueContainer: (base) => ({ ...base, padding: "0 8px" }),
                    indicatorsContainer: (base) => ({ ...base, display: "none" }),
                    input: (base) => ({ ...base, margin: 0, padding: 0 }),
                    menu: (base) => ({ ...base, zIndex: 10000, width: "280px" }),
                    menuPortal: (base) => ({ ...base, zIndex: 100000 }),
                  }}
                />
                <button
                  type="button"
                  className="mail-recipient-add-btn"
                  onClick={() => selectedUser?.value && handleAdd(selectedUser.value)}
                  title="Add recipient"
                  disabled={!selectedUser}
                >
                  +
                </button>
              </div>
            )}
            {inputError && <span className="mail-recipient-error">{inputError}</span>}
          </div>
        )}
      </div>
    </>
  );
}

export default MailRecipientsCell;
